import {
    TRecipientInfo,
    TRecipientState,
} from '@/src/modules/build-a-card/inputs/types'
import {
    ADD_CARD,
    REMOVE_CARD,
    REMOVE_RECIPIENT,
    SET_ALGOLIA_STATE,
    SET_BRAND_STATE,
    SET_DELIVERY_TYPE,
    SET_GIFT_STATE,
    SET_RECIPIENT,
    VALIDATE_RECIPIENTS,
} from '@/src/modules/build-a-card/constants'
import { decrement, increment } from '@/src/common/utilities/setQuantity'
import {
    recipientEmailSchema,
    recipientPhoneSchema,
} from '@/src/modules/build-a-card/validations/schemas'
import { EMAIL_DELIVERY, TEXT_DELIVERY } from '@/src/modules/pg-plus/constants'

type Payload = {
    recipientIndex: number
}

type SetRecipientPayload = Payload & {
    recipientName?: string | null
    recipientEmail?: string | null
    recipientPhone?: string | null
}

// For PG Plus Ordering
type SetBrandPayload = {
    brandName: string
    brandSlug: string
    brandUuid: string
    brandCategories: any[]
    brandImage: string
    isPgPlus: boolean
}

type SetAlgoliaPayload = {
    brandQueryId: string
    brandObjectId: string
}

type SetGiftStatePayload = {
    isEditing: boolean
    listOfRecipients: TRecipientInfo[]
    cardQuantity: number
}

type SetDeliveryTypePayload = {
    type: typeof EMAIL_DELIVERY | typeof TEXT_DELIVERY
}

type SetRecipient = {
    type: typeof SET_RECIPIENT
    payload?: SetRecipientPayload
}

type AddCard = {
    type: typeof ADD_CARD
    payload: Payload
}

type RemoveCard = {
    type: typeof REMOVE_CARD
    payload: Payload
}

type RemoveRecipient = {
    type: typeof REMOVE_RECIPIENT
    payload: Payload
}

type SetGiftState = {
    type: typeof SET_GIFT_STATE
    payload: SetGiftStatePayload
}

type SetBrandState = {
    type: typeof SET_BRAND_STATE
    payload: SetBrandPayload
}

type SetAlgoliaState = {
    type: typeof SET_ALGOLIA_STATE
    payload: SetAlgoliaPayload
}

type SetDeliveryType = {
    type: typeof SET_DELIVERY_TYPE
    payload: SetDeliveryTypePayload
}

type ValidateRecipients = {
    type: typeof VALIDATE_RECIPIENTS
    payload: {}
}

type Actions =
    | SetRecipient
    | AddCard
    | RemoveCard
    | RemoveRecipient
    | SetGiftState
    | ValidateRecipients
    | SetBrandState
    | SetDeliveryType
    | SetAlgoliaState

export function recipientReducer(state: TRecipientState, action: Actions) {
    const { type, payload } = action
    const {
        minValue,
        maxValue,
        cardQuantity,
        setCardQuantity,
        setListOfRecipients,
    } = state

    const recipientSchema = state?.sendingViaEmail
        ? recipientEmailSchema
        : recipientPhoneSchema

    switch (type) {
        case ADD_CARD:
            increment({
                minValue: minValue,
                maxValue: maxValue,
                quantity: cardQuantity,
                setQuantity: setCardQuantity,
                setListOfRecipients: setListOfRecipients,
            }).then(() => {})
            return state
        case REMOVE_CARD:
            decrement({
                minValue: minValue,
                maxValue: maxValue,
                quantity: cardQuantity,
                setQuantity: setCardQuantity,
                setListOfRecipients: setListOfRecipients,
            }).then(() => {})
            return state
        case SET_RECIPIENT:
            const index: number = payload?.recipientIndex ?? 0
            state?.setListOfRecipients((previous: TRecipientInfo[]) => {
                const newList = [...previous]

                newList[index] = {
                    recipientName: payload?.recipientName ?? '',
                    recipientEmail: payload?.recipientEmail ?? '',
                    recipientPhone: payload?.recipientPhone ?? '',
                    recipientErrorMessages: [],
                    ...newList,
                }
                return [...newList]
            })

            return state
        case REMOVE_RECIPIENT:
            setListOfRecipients((previous: TRecipientInfo[]) => {
                const newList = [...previous]
                newList.splice(payload?.recipientIndex, 1)
                return [...newList]
            })
            setCardQuantity(cardQuantity - 1)
            return state
        case SET_GIFT_STATE:
            return {
                ...state,
                isEditing: payload?.isEditing ?? state.isEditing,
                listOfRecipients:
                    payload?.listOfRecipients ?? state.listOfRecipients,
                cardQuantity: payload?.cardQuantity ?? state.cardQuantity,
            }
        case SET_BRAND_STATE:
            return {
                ...state,
                brandName: payload.brandName,
                brandSlug: payload.brandSlug,
                brandUuid: payload.brandUuid,
                brandCategories: payload.brandCategories,
                brandImage: payload.brandImage,
                isPgPlus: payload.isPgPlus,
            }
        case SET_ALGOLIA_STATE:
            return {
                ...state,
                brandObjectId: payload.brandObjectId,
                brandQueryId: payload.brandQueryId,
            }
        case SET_DELIVERY_TYPE: {
            const isEmailDelivery: boolean = payload.type === EMAIL_DELIVERY

            function clearAltValue() {
                const newList = [...state.listOfRecipients]
                newList.forEach((recipient) => {
                    isEmailDelivery
                        ? (recipient.recipientPhone = '')
                        : (recipient.recipientEmail = '')
                })

                return [...newList]
            }

            return {
                ...state,
                deliveryType: payload.type,
                sendingViaEmail: Boolean(payload.type === EMAIL_DELIVERY),
                listOfRecipients: clearAltValue(),
            }
        }

        case VALIDATE_RECIPIENTS:
            // check that all required recipient information has been filled out

            const hasAllRecipients: boolean =
                state.listOfRecipients.filter(
                    (recipient) =>
                        recipient.recipientHasError ||
                        !recipient.recipientName ||
                        (!recipient.recipientEmail && !recipient.recipientPhone)
                ).length === 0

            // validate the recipient information
            let recIndex = 0
            for (const recipient of state.listOfRecipients) {
                const validation = state.sendingViaEmail
                    ? {
                          recipientName: recipient?.recipientName,
                          recipientEmail: recipient?.recipientEmail,
                          deliveryMethod: state.deliveryType,
                      }
                    : {
                          recipientName: recipient?.recipientName,
                          recipientPhone: recipient?.recipientPhone,
                          deliveryMethod: state.deliveryType,
                      }

                recipientSchema
                    .validate(validation, {
                        abortEarly: false,
                        context: {
                            checkPhoneBlocked: true,
                        },
                    })
                    .then(() => {
                        state.listOfRecipients[recIndex].recipientHasError =
                            false
                        state.listOfRecipients[
                            recIndex
                        ].recipientErrorMessages = []
                    })
                    .catch((err) => {
                        if (state.listOfRecipients[recIndex]) {
                            state.listOfRecipients[recIndex].recipientHasError =
                                true
                            const errorMessagesArray =
                                state.listOfRecipients[recIndex]
                                    .recipientErrorMessages
                            if (!errorMessagesArray.includes(err.inner[0])) {
                                state.listOfRecipients[
                                    recIndex
                                ].recipientErrorMessages.push(err.inner[0])
                            }
                        }
                    })
                recIndex++
            }
            return { ...state, formIsValid: hasAllRecipients }
        default:
            return state
    }
}
